import { useState } from "react";
import { Loader2, ArrowUpRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const EMPTY_FORM = { name: "", email: "", phone: "", address_line1: "", address_line2: "", city: "", postal_code: "" };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const fieldClass = "mt-1.5 bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-[var(--brand-primary)]/60";

// One quick step collected on-site before handing off to Stripe — this is
// what actually lets the Abandoned Carts admin report and cart-recovery
// emails work, since Stripe's own hosted checkout doesn't reliably expose
// contact details for a session that wasn't completed.
export const CheckoutDetailsDialog = ({ open, onOpenChange, onSubmit, submitting, error, submitLabel = "Continue to Payment" }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Required";
    if (!EMAIL_RE.test(form.email.trim())) next.email = "Enter a valid email";
    if (!form.phone.trim()) next.phone = "Required";
    if (!form.address_line1.trim()) next.address_line1 = "Required";
    if (!form.city.trim()) next.city = "Required";
    if (!form.postal_code.trim()) next.postal_code = "Required";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit({
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      address_line1: form.address_line1.trim(),
      address_line2: form.address_line2.trim() || undefined,
      city: form.city.trim(),
      postal_code: form.postal_code.trim(),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="checkout-details-dialog" className="max-w-md bg-[var(--bg-secondary)] border-[var(--border-default)] text-white max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white font-display text-2xl tracking-wide">Your Details</DialogTitle>
          <DialogDescription className="text-[var(--text-secondary)]">
            We need this to process your order and keep you posted on delivery. Payment itself is entered next, on Stripe's own secure page.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div>
            <Label htmlFor="checkout-name" className="text-white/80">Full Name</Label>
            <Input id="checkout-name" data-testid="checkout-details-name" value={form.name} onChange={update("name")} className={fieldClass} placeholder="Jane Smith" />
            {errors.name && <p data-testid="checkout-details-name-error" className="text-red-400 text-xs mt-1">{errors.name}</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="checkout-email" className="text-white/80">Email</Label>
              <Input id="checkout-email" type="email" data-testid="checkout-details-email" value={form.email} onChange={update("email")} className={fieldClass} placeholder="jane@example.com" />
              {errors.email && <p data-testid="checkout-details-email-error" className="text-red-400 text-xs mt-1">{errors.email}</p>}
            </div>
            <div>
              <Label htmlFor="checkout-phone" className="text-white/80">Phone</Label>
              <Input id="checkout-phone" type="tel" data-testid="checkout-details-phone" value={form.phone} onChange={update("phone")} className={fieldClass} placeholder="021 123 4567" />
              {errors.phone && <p data-testid="checkout-details-phone-error" className="text-red-400 text-xs mt-1">{errors.phone}</p>}
            </div>
          </div>
          <div>
            <Label htmlFor="checkout-address1" className="text-white/80">Address</Label>
            <Input id="checkout-address1" data-testid="checkout-details-address1" value={form.address_line1} onChange={update("address_line1")} className={fieldClass} placeholder="Street address" />
            {errors.address_line1 && <p data-testid="checkout-details-address1-error" className="text-red-400 text-xs mt-1">{errors.address_line1}</p>}
          </div>
          <Input data-testid="checkout-details-address2" value={form.address_line2} onChange={update("address_line2")} className={`${fieldClass} mt-0`} placeholder="Apartment, suite, etc. (optional)" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="checkout-city" className="text-white/80">City</Label>
              <Input id="checkout-city" data-testid="checkout-details-city" value={form.city} onChange={update("city")} className={fieldClass} placeholder="Christchurch" />
              {errors.city && <p data-testid="checkout-details-city-error" className="text-red-400 text-xs mt-1">{errors.city}</p>}
            </div>
            <div>
              <Label htmlFor="checkout-postcode" className="text-white/80">Postcode</Label>
              <Input id="checkout-postcode" data-testid="checkout-details-postcode" value={form.postal_code} onChange={update("postal_code")} className={fieldClass} placeholder="8011" />
              {errors.postal_code && <p data-testid="checkout-details-postcode-error" className="text-red-400 text-xs mt-1">{errors.postal_code}</p>}
            </div>
          </div>
          <p className="text-xs text-[var(--text-secondary)] pt-1">Shipping within New Zealand only.</p>
          {error && <p data-testid="checkout-details-error" className="text-red-400 text-sm">{error}</p>}
          <button type="submit" disabled={submitting} data-testid="checkout-details-submit" className="btn-primary w-full justify-center disabled:opacity-70">
            {submitting ? <><Loader2 size={14} className="animate-spin" /> Continuing…</> : <>{submitLabel} <ArrowUpRight size={14} /></>}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
